// src/pages/driverReports/OrphanedIncomeManager.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { updateDoc, doc, collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { loadOrphanedIncome as loadOrphanedIncomeFromUtils } from "./carSheets/orphanedIncomeUtils";

function toDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value instanceof Date) return value;
  if (value.seconds !== undefined) return new Date(value.seconds * 1000);
  if (typeof value === "string") return new Date(value);
  try {
    return new Date(value);
  } catch {
    return null;
  }
}

function formatDate(value) {
  const d = toDate(value);
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

function amountText(value) {
  return `AED ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function sheetDriverIds(sheet = {}) {
  return Object.keys(sheet.driverPeriods || {}).filter(Boolean);
}

function sheetDriversText(sheet = {}) {
  const names = Array.isArray(sheet.drivers) ? sheet.drivers.filter(Boolean) : [];
  if (names.length) return names.join(" + ");

  const periods = sheet.driverPeriods || {};
  const fromPeriods = Object.values(periods)
    .flat()
    .map((p) => p.driverName)
    .filter(Boolean);
  return fromPeriods.join(" + ");
}

function sheetLabel(sheet = {}) {
  const period = `${formatDate(sheet.periodStart)} → ${formatDate(sheet.periodEnd)}`;
  const drivers = sheetDriversText(sheet) || "No driver name";
  return `${sheet.carNumber || "Unknown car"} | ${period} | ${drivers}`;
}

function itemKey(item) {
  return `${item.type}_${item.id}`;
}

export default function OrphanedIncomeManager() {
  const { month } = useParams();
  const navigate = useNavigate();

  const [orphanedIncome, setOrphanedIncome] = useState({ weekly: [], manual: [] });
  const [carSheets, setCarSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSheetByItem, setSelectedSheetByItem] = useState({});
  const [showAssigned, setShowAssigned] = useState(true);

  const loadCarSheets = async () => {
    const snap = await getDocs(collection(db, "carSheets", month, "sheets"));
    const sheets = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const car = String(a.carNumber || "").localeCompare(String(b.carNumber || ""));
        if (car !== 0) return car;
        return (toDate(a.periodStart)?.getTime() || 0) - (toDate(b.periodStart)?.getTime() || 0);
      });
    setCarSheets(sheets);
  };

  // Load data once month changes
  useEffect(() => {
    const loadData = async () => {
      if (!month) return;
      setLoading(true);
      try {
        const data = await loadOrphanedIncomeFromUtils(month, {
          includePending: true,
          includeAssigned: true,
        });
        setOrphanedIncome(data);
        await loadCarSheets();
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  // Reload helper
  const reload = async () => {
    setLoading(true);
    const data = await loadOrphanedIncomeFromUtils(month, {
      includePending: true,
      includeAssigned: true,
    });
    setOrphanedIncome(data);
    await loadCarSheets();
    setLoading(false);
  };

  // Assign orphaned income to exact built car sheet
  const assignToSheet = async (item, sheetId) => {
    if (!sheetId) {
      alert("Please select the exact built car sheet first.");
      return;
    }

    const selectedSheet = carSheets.find((s) => s.id === sheetId);
    if (!selectedSheet) {
      alert("Selected sheet was not found. Please reload and try again.");
      return;
    }

    const drivers = sheetDriversText(selectedSheet);
    const confirmText = [
      "Assign this orphaned income to this exact car sheet?",
      "",
      `Income driver: ${item.driverName || "Unknown"}`,
      `Platform/type: ${item.platform || item.planType || "N/A"}`,
      `Week/date: ${item.week || item.date || "N/A"}`,
      `Amount: ${amountText(item.amount)}`,
      "",
      `Selected sheet: ${sheetLabel(selectedSheet)}`,
      drivers ? "" : "Warning: selected sheet has no visible driver name.",
    ].join("\n");

    if (!window.confirm(confirmText)) return;

    try {
      const type = item.type === "weeklyEarnings" ? "weeklyEarnings" : "manualTrips";
      const ref = doc(db, "orphanedIncome", month, type, item.id);
      const ids = sheetDriverIds(selectedSheet);

      await updateDoc(ref, {
        status: "assigned",
        assignedToCar: selectedSheet.carNumber || "",
        assignedToSheetId: selectedSheet.id,
        assignedToSheetCarNumber: selectedSheet.carNumber || "",
        assignedToSheetCompany: selectedSheet.company || "",
        assignedToSheetCarModel: selectedSheet.carModel || "",
        assignedToSheetPlanType: selectedSheet.planType || "",
        assignedToSheetPeriodStart: toDate(selectedSheet.periodStart),
        assignedToSheetPeriodEnd: toDate(selectedSheet.periodEnd),
        assignedToSheetDriverIds: ids,
        assignedToSheetDrivers: Array.isArray(selectedSheet.drivers) ? selectedSheet.drivers : [],
        assignedAt: new Date(),
        processed: true,
      });

      alert(`✅ Assigned to exact sheet: ${selectedSheet.carNumber}`);
      reload();
    } catch (err) {
      console.error("Assign error:", err);
      alert("❌ Failed to assign");
    }
  };

  // Ignore orphaned income
  const ignoreIncome = async (item) => {
    if (!window.confirm("Ignore this orphaned income? It will not be included in car sheets.")) return;

    try {
      const type = item.type === "weeklyEarnings" ? "weeklyEarnings" : "manualTrips";
      const ref = doc(db, "orphanedIncome", month, type, item.id);

      await updateDoc(ref, {
        status: "ignored",
        ignoredAt: new Date(),
        processed: true,
      });

      alert("✅ Ignored");
      reload();
    } catch (err) {
      console.error("Ignore error:", err);
      alert("❌ Failed to ignore");
    }
  };

  const visibleWeekly = orphanedIncome.weekly.filter((x) => showAssigned || (x.status || "pending") !== "assigned");
  const visibleManual = orphanedIncome.manual.filter((x) => showAssigned || (x.status || "pending") !== "assigned");

  if (loading) {
    return <div style={{ padding: 20 }}>Loading orphaned income...</div>;
  }

  const totalWeekly = visibleWeekly.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
  const totalManual = visibleManual.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
  const totalAmount = totalWeekly + totalManual;

  const renderSheetSelect = (item) => {
    const key = itemKey(item);
    const selected = selectedSheetByItem[key] || item.assignedToSheetId || "";

    const recommended = carSheets.filter((s) => {
      const ids = sheetDriverIds(s);
      return ids.includes(item.driverId) || sheetDriversText(s).toLowerCase().includes(String(item.driverName || "").toLowerCase());
    });

    const other = carSheets.filter((s) => !recommended.some((r) => r.id === s.id));
    const options = [...recommended, ...other];

    return (
      <select
        value={selected}
        onChange={(e) => setSelectedSheetByItem((prev) => ({ ...prev, [key]: e.target.value }))}
        style={{ padding: 7, minWidth: 420, maxWidth: 520, border: "1px solid #ccc", borderRadius: 4 }}
      >
        <option value="">Select exact built car sheet...</option>
        {options.map((s, idx) => (
          <option key={s.id} value={s.id}>
            {recommended.some((r) => r.id === s.id) && idx < recommended.length ? "★ " : ""}{sheetLabel(s)}
          </option>
        ))}
      </select>
    );
  };

  const renderStatus = (item) => {
    const status = item.status || "pending";
    if (status === "assigned") {
      return (
        <span style={{ color: "#0f766e", fontWeight: 700 }}>
          Assigned{item.assignedToSheetId ? " to sheet" : item.assignedToCar ? " to car only" : ""}
        </span>
      );
    }
    return <span style={{ color: "#b45309", fontWeight: 700 }}>Pending</span>;
  };

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <h2>⚠️ Orphaned Income Manager — {month}</h2>
        <button onClick={() => navigate(-1)} style={backButton}>← Back</button>
      </div>

      {/* Summary */}
      <div style={summaryBox(totalAmount)}>
        <h3 style={{ margin: 0 }}>
          Visible orphaned amount:
          <span style={{ marginLeft: 10, color: totalAmount > 0 ? "#d35400" : "#155724" }}>
            {amountText(totalAmount)}
          </span>
        </h3>
        <p style={{ marginTop: 10 }}>
          <strong>{visibleWeekly.length}</strong> Weekly Earnings ({amountText(totalWeekly)})
          &nbsp; | &nbsp;
          <strong>{visibleManual.length}</strong> Manual Trips ({amountText(totalManual)})
          &nbsp; | &nbsp;
          <strong>{carSheets.length}</strong> built car sheets available for assignment
        </p>
        <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={showAssigned} onChange={(e) => setShowAssigned(e.target.checked)} />
          Show already assigned rows also
        </label>
      </div>

      <div style={noteBox}>
        Assign by selecting the exact built car sheet, not only the car number. This prevents income going to the wrong
        driver period when the same car has multiple sheets in the month.
      </div>

      {/* WEEKLY TABLE */}
      {visibleWeekly.length > 0 && (
        <div style={{ marginBottom: 40 }}>
          <h3>Weekly Earnings ({visibleWeekly.length})</h3>
          <table className="orphan-table" style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Driver</th>
                <th style={th}>Platform</th>
                <th style={th}>Week</th>
                <th style={th}>Amount</th>
                <th style={th}>Status</th>
                <th style={th}>Assign to exact built sheet</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleWeekly.map((item) => {
                const key = itemKey(item);
                return (
                  <tr key={key}>
                    <td style={td}>{item.driverName || "Unknown"}</td>
                    <td style={td}>{item.platform || "N/A"}</td>
                    <td style={td}>{item.week}</td>
                    <td style={{ ...td, fontWeight: "bold" }}>{amountText(item.amount)}</td>
                    <td style={td}>{renderStatus(item)}</td>
                    <td style={td}>{renderSheetSelect(item)}</td>
                    <td style={td}>
                      <button onClick={() => assignToSheet(item, selectedSheetByItem[key] || item.assignedToSheetId)} style={assignButton(true)}>
                        Assign Sheet
                      </button>
                      <button onClick={() => ignoreIncome(item)} style={ignoreButton}>Ignore</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* MANUAL TABLE */}
      {visibleManual.length > 0 && (
        <div>
          <h3>Manual Trips ({visibleManual.length})</h3>
          <table className="orphan-table" style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Driver</th>
                <th style={th}>Date</th>
                <th style={th}>Plan</th>
                <th style={th}>Amount</th>
                <th style={th}>Status</th>
                <th style={th}>Assign to exact built sheet</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleManual.map((item) => {
                const key = itemKey(item);
                return (
                  <tr key={key}>
                    <td style={td}>{item.driverName || "Unknown"}</td>
                    <td style={td}>{item.date}</td>
                    <td style={td}>{item.planType}</td>
                    <td style={{ ...td, fontWeight: "bold" }}>{amountText(item.amount)}</td>
                    <td style={td}>{renderStatus(item)}</td>
                    <td style={td}>{renderSheetSelect(item)}</td>
                    <td style={td}>
                      <button onClick={() => assignToSheet(item, selectedSheetByItem[key] || item.assignedToSheetId)} style={assignButton(true)}>
                        Assign Sheet
                      </button>
                      <button onClick={() => ignoreIncome(item)} style={ignoreButton}>Ignore</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {visibleWeekly.length === 0 && visibleManual.length === 0 && (
        <div style={{ textAlign: "center", padding: 30, background: "#d4edda", borderRadius: 8 }}>
          <h3 style={{ color: "#155724" }}>✅ No visible orphaned income</h3>
        </div>
      )}
    </div>
  );
}

const tableStyle = { borderCollapse: "collapse", width: "100%", background: "white" };
const th = { padding: 9, border: "1px solid #ddd", background: "#f1f5f9", textAlign: "left" };
const td = { padding: 9, border: "1px solid #ddd", verticalAlign: "top" };

const backButton = {
  padding: 8,
  background: "#6c757d",
  color: "white",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
};

const summaryBox = (totalAmount) => ({
  padding: 20,
  borderRadius: 8,
  background: totalAmount !== 0 ? "#fff3cd" : "#d4edda",
  border: "1px solid #ffeaa7",
  marginBottom: 18,
});

const noteBox = {
  background: "#e0f2fe",
  border: "1px solid #7dd3fc",
  color: "#075985",
  padding: 14,
  borderRadius: 6,
  marginBottom: 30,
};

const assignButton = (canAssign) => ({
  padding: "6px 12px",
  marginRight: 8,
  background: canAssign ? "#28a745" : "#6c757d",
  color: "white",
  borderRadius: 4,
  border: "none",
  cursor: canAssign ? "pointer" : "not-allowed",
});

const ignoreButton = {
  padding: "6px 12px",
  background: "#dc3545",
  color: "white",
  borderRadius: 4,
  border: "none",
  cursor: "pointer",
};
